import * as z from 'zod/v4';

import { invertObject, zodEnumFromObjectKeys } from '../utils/entry.js';

/**
 * Sport types
 */
export const SPORT_TYPE_VARIANTS = {
  Undefined: 0, // Ospecificerad
  Soccer: 1, // Fotboll
  IceHockey: 2, // Ishockey
  Bandy: 3, // Bandy
  Handball: 4, // Handboll
  Floorball: 5, // Innebandy
  Tennis: 6, // Tennis
  Golf: 7, // Golf
  Speedway: 8, // Speedway
  Basketball: 10, // Basket
  SkiingAlpine: 11, // Alpint
  Formula1: 14, // Formel 1
  Athletics: 15, // Friidrott
  SkiingCrossCountry: 17, // Längdskidåkning
  WinterOlympic: 26, // Vinter OS
  Biathlon: 27, // Skidskytte
  Baseball: 39, // Baseboll
  MartialArt: 41, // Kampsport
  Trotting: 58, // Trav
  Specialties: 59, // Specialiteter
  SummerOlympic: 63, // Sommar OS
  AmericanFootball: 89, // Amerikansk fotboll (NFL)
  // Legacy types
  Curling: 9,
  SkiJumping: 12,
  AmerikanskFotboll: 13,
  Rugby: 16,
  Melodifestivalen: 18,
  Cykel: 19,
  Simning: 20,
  Bordtennis: 21,
  Beachvolleyboll: 22,
  Indycar: 23,
  Boxning: 24,
  Livespel: 25,
  Skridsko: 28,
  NordiskKomb: 29,
  Freestyle: 30,
  Snowboard: 31,
  Orientering: 32,
  Dans: 33,
  Rally: 34,
  Snooker: 35,
  Os2012: 36,
  Konstakning: 37,
  Rallycross: 38,
  Nascar: 40,
  Isracing: 42,
  InlineHockey: 43,
  AussieRules: 44,
  Badminton: 45,
  Strandfotboll: 46,
  Futsal: 47,
  Boboll: 48,
  Vattenpolo: 49,
  Dart: 50,
  Motorsport: 51,
  Vintersporter: 52,
  Landhockey: 53,
  Lacrosse: 54,
  Horseball: 55,
  OlympicsYouth: 56,
  Surfing: 57,
  Cricket: 60,
  Biljard: 61,
  GaelicSport: 62,
  Bowling: 64,
  Schack: 65,
  Netball: 66,
  Squash: 67,
  Rullskridskohockey: 68,
  Motorcykel: 69,
  DTM: 70,
  Bob: 71,
  SpeedSkating: 72,
  Rodel: 73,
  Softball: 74,
  Schwingen: 75,
  Greyhound: 76,
  Gatuhockey: 77,
  VM: 78,
  Rodd: 79,
  SnowboardAFU: 80,
  MotoGP: 81,
  Moto2: 82,
  SnowboardASP: 83,
  _125cc: 84,
  Padel: 85,
  Kanot: 86,
  Cykelbana: 87,
  Volleyboll: 88,
  Triathlon: 90,
  Segling: 91,
  Skicross: 92,
  UndefinedSport93: 93,
  UndefinedSport94: 94,
  UndefinedSport95: 95,
  UndefinedSport96: 96,
  Shorttrack: 97,
  Skeleton: 98,
  UndefinedSport99: 99,
  UndefinedSport100: 100,
  UndefinedSport101: 101,
  UndefinedSport102: 102,
} as const;

/**
 * Zod enum for SportType entity
 */
export const sportTypeId = z.enum(SPORT_TYPE_VARIANTS);

/**
 * TS type for sport type id
 */
export type SportTypeId = z.infer<typeof sportTypeId>;

/**
 * Zod enum for SportType entity
 */
export const sportType = zodEnumFromObjectKeys(SPORT_TYPE_VARIANTS);

/**
 * TS type for sport type
 */
export type SportType = z.infer<typeof sportType>;

/**
 * TS type for sport type variants
 */
export type Sport = z.infer<typeof sportSchema>;

/**
 * Object mapping between numeric sport type id and sport type
 */
export const SPORT_TYPE_ID_VARIANTS = invertObject<SportType, SportTypeId>(SPORT_TYPE_VARIANTS);

const sportIdSchema = z.number().int().nonnegative().brand<'SportId'>();

export type SportId = z.infer<typeof sportIdSchema>;

/**
 * Zod schema for Sport entity
 */
export const sportSchema = z
  .object({
    type: sportTypeId,
    name: z.string(),
    id: sportIdSchema,
    numCountries: z.number().optional(),
  })
  .transform((sport) => {
    return {
      ...sport,
      type: SPORT_TYPE_ID_VARIANTS[sport.type],
    };
  });
